/**
 * Guest identity storage — persists across browser tab closes with an expiry.
 *
 * Why localStorage (not sessionStorage)?
 *   Guests routinely close the QR-scanned tab and re-scan later that same
 *   night. sessionStorage clears on tab close, forcing them to re-enter
 *   name/phone (and re-do SMS OTP) every single time. localStorage with a
 *   6-hour expiry matches the backend `guest_token` TTL so re-verification
 *   only kicks in once the token itself would be invalid anyway.
 *
 * Values are wrapped as { v, exp } to attach a per-key expiry stamp.
 * Legacy raw payloads (no wrapper) are still readable so we degrade
 * gracefully during rollout.
 */
const DEFAULT_TTL_HOURS = 6;

export const guestSet = (key, value, ttlHours = DEFAULT_TTL_HOURS) => {
  const record = {
    v: value,
    exp: Date.now() + ttlHours * 60 * 60 * 1000,
  };
  try {
    localStorage.setItem(key, JSON.stringify(record));
  } catch (err) {
    // Storage disabled (private mode etc.) — swallow, callers handle nulls.
  }
};

export const guestGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // New wrapper format
    if (parsed && typeof parsed === 'object' && 'v' in parsed && 'exp' in parsed) {
      if (parsed.exp && parsed.exp < Date.now()) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
        return null;
      }
      return parsed.v;
    }
    // Legacy payload (no wrapper) — return as-is
    return parsed;
  } catch {
    return null;
  }
};

export const guestGetString = (key) => {
  const v = guestGet(key);
  if (typeof v === 'string') return v;
  if (v == null) return '';
  return String(v);
};

export const guestSetString = (key, value, ttlHours = DEFAULT_TTL_HOURS) => {
  guestSet(key, String(value ?? ''), ttlHours);
};

export const guestRemove = (key) => {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
};
